import { Button, Input, Modal, Typography } from 'antd';
import { type FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PreviewGenerationStatus } from '@shared/api';
import { normalizePreviewUrl } from '../lib/preview-url';
import styles from '../preview.module.scss';

interface PreviewGenerateModalProps {
  open: boolean;
  isGenerating: boolean;
  onClose: () => void;
  onGenerate: (url: string) => Promise<PreviewGenerationStatus>;
}

type GenerateError = 'invalid' | 'exists' | 'request' | null;

export const PreviewGenerateModal = ({
  open,
  isGenerating,
  onClose,
  onGenerate,
}: PreviewGenerateModalProps) => {
  const { t } = useTranslation('translation', { keyPrefix: 'preview.generate' });
  const [url, setUrl] = useState('');
  const [error, setError] = useState<GenerateError>(null);

  useEffect(() => {
    if (!open) {
      setUrl('');
      setError(null);
    }
  }, [open]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const normalizedUrl = normalizePreviewUrl(url);
    if (!normalizedUrl) {
      setError('invalid');
      return;
    }

    setError(null);
    try {
      const status = await onGenerate(normalizedUrl);
      if (status === 'exists') setError('exists');
    } catch {
      setError('request');
    }
  };

  return (
    <Modal
      centered
      open={open}
      title={t('title')}
      width={480}
      footer={null}
      closable={!isGenerating}
      keyboard={!isGenerating}
      maskClosable={!isGenerating}
      className={styles.generateModal}
      onCancel={onClose}
    >
      <form className={styles.generateForm} onSubmit={handleSubmit}>
        <label className={styles.field}>
          <Typography.Text strong>{t('url')}</Typography.Text>
          <Input
            autoFocus
            size="large"
            value={url}
            disabled={isGenerating}
            status={error ? 'error' : undefined}
            placeholder={t('placeholder')}
            onChange={(event) => {
              setUrl(event.target.value);
              if (error) setError(null);
            }}
          />
          {error && (
            <Typography.Text type="danger" className={styles.fieldError} role="alert">
              {t(error)}
            </Typography.Text>
          )}
        </label>

        <div className={styles.modalActions}>
          <Button size="large" disabled={isGenerating} onClick={onClose}>
            {t('cancel')}
          </Button>
          <Button
            type="primary"
            size="large"
            htmlType="submit"
            loading={isGenerating}
            disabled={!url.trim()}
          >
            {t('submit')}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
